import { useState } from 'react';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { api } from '../api/client';

// Simpler bootstrap wizard than Quick Dial's: takes a manually-pasted AAD
// group ID instead of listing groups from Graph. Port the Quick Dial picker
// over once you have the curator-mgmt UI built.
export function BootstrapWizard({ onComplete }: { onComplete: () => void }) {
  const [groupId, setGroupId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const trimmed = groupId.trim();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) {
      setError("That doesn't look like a valid AAD group GUID");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.bootstrap(trimmed);
      onComplete();
    } catch (e: any) {
      setError(e.message || 'Setup failed');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-lg border border-gray-200 p-6">
        <h1 className="text-xl font-semibold text-gray-900 mb-1">Welcome to Slide Library</h1>
        <p className="text-sm text-gray-600 mb-5">
          Paste the object ID of the Microsoft Entra security group whose members will be Slide Library curators (typically your Comms or Marketing team).
        </p>

        <a
          href="https://entra.microsoft.com/#view/Microsoft_AAD_IAM/GroupsManagementMenuBlade/~/AllGroups"
          target="_blank"
          rel="noopener"
          className="inline-flex items-center gap-1 text-xs text-accent-600 hover:underline mb-4"
        >
          Open Entra Groups <ExternalLink className="w-3 h-3" />
        </a>

        <input
          type="text"
          value={groupId}
          onChange={e => setGroupId(e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 mb-3"
        />

        {error && (
          <div className="mb-3 px-3 py-2 bg-red-50 border border-red-100 rounded text-xs text-red-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            You must be a Global Administrator to complete this step.
          </p>
          <button
            onClick={submit}
            disabled={!groupId.trim() || submitting}
            className="px-4 py-2 text-sm bg-accent-600 hover:bg-accent-700 disabled:bg-gray-300 text-white rounded font-medium"
          >
            {submitting ? 'Setting up…' : 'Complete setup'}
          </button>
        </div>
      </div>
    </div>
  );
}
